import React, { useState, useRef, useEffect } from "react";
import Switch from "react-switch";
import { createRoot } from "react-dom/client";
import QRCode from "qrcode.react";

const PasswordGeneratorComponent = (props) => {
    const {
        titleLength,
        titleUppercase,
        titleLowercase,
        titleDigits,
        titleHyphen,
        titleUnderscore,
        titleSpace,
        titleSpecialCharacters,
        titleGeneratePasswords,
        titlePassword,
        titleAction,
        titleCopy,
        titleCopied,
        titleEasy,
        titleMedium,
        titleHard,
        tip1,
    } = props.attributes;

    const [length, setLength] = useState(8);
    const [shouldGenerate, setShouldGenerate] = useState(false);
    const [options, setOptions] = useState({
        uppercase: true,
        lowercase: true,
        digits: true,
        hyphen: false,
        underscore: false,
        space: false,
        specialCharacters: false,
    });
    const [customCharacters, setCustomCharacters] = useState("");

    const characters = {
        uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
        lowercase: "abcdefghijklmnopqrstuvwxyz",
        digits: "0123456789",
        hyphen: "-",
        underscore: "_",
        space: " ",
        specialCharacters: "!@#$%^&*(){}[]=<>/,.",
    };

    const [passwords, setPasswords] = useState([]);
    const [shouldScroll, setShouldScroll] = useState(false);
    const tableRef = useRef(null);

    const handleGeneratePasswords = () => {
        let pool = "";
        let characterSets = [];

        for (const option in options) {
            if (options[option]) {
                pool += characters[option];
                characterSets.push(characters[option]);
            }
        }

        if (customCharacters.trim()) {
            pool += customCharacters.trim();
            characterSets.push(customCharacters.trim());
        }

        if (pool.length === 0) {
            /* alert(
                "Пожалуйста, выберите хотя бы один набор символов или введите пользовательские символы."
            ); */
            return;
        }

        let newPasswords = [];

        for (let j = 0; j < 10; j++) {
            let passwordArray = [];

            // Гарантировать наличие хотя бы одного символа из каждого набора
            characterSets.forEach((set) => {
                passwordArray.push(set[Math.floor(Math.random() * set.length)]);
            });

            // Заполнить оставшуюся часть пароля
            while (passwordArray.length < length) {
                passwordArray.push(
                    pool[Math.floor(Math.random() * pool.length)]
                );
            }

            // Перемешать символы пароля
            passwordArray.sort(() => Math.random() - 0.5);

            newPasswords.push(passwordArray.join(""));
        }

        setPasswords(newPasswords);
        setShouldScroll(true);
    };

    useEffect(() => {
        if (shouldGenerate) {
            handleGeneratePasswords();
            setShouldGenerate(false);
        }
    }, [options, shouldGenerate]);

    const handleEasyPasswords = () => {
        setOptions({
            uppercase: false,
            lowercase: true,
            digits: false,
            hyphen: false,
            underscore: false,
            space: false,
            specialCharacters: false,
        });
        setShouldGenerate(true);
    };

    const handleMediumPasswords = () => {
        setOptions({
            uppercase: true,
            lowercase: true,
            digits: true,
            hyphen: false,
            underscore: false,
            space: false,
            specialCharacters: false,
        });
        setShouldGenerate(true);
    };

    const handleHardPasswords = () => {
        setOptions({
            uppercase: true,
            lowercase: true,
            digits: true,
            hyphen: true,
            underscore: true,
            space: true,
            specialCharacters: true,
        });
        setShouldGenerate(true);
    };

    useEffect(() => {
        if (shouldScroll && tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: "smooth" });
            setShouldScroll(false);
        }
    }, [passwords, shouldScroll]);

    const [copiedIndex, setCopiedIndex] = useState(null);

    const handleCopyToClipboard = (password, index) => {
        navigator.clipboard.writeText(password);
        setCopiedIndex(index);

        setTimeout(() => {
            setCopiedIndex(null);
        }, 5000);
    };

    const [qrCodeColumnStyle, setQrCodeColumnStyle] = useState({
        display: "none",
    });

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1270) {
                setQrCodeColumnStyle({ display: "table-cell" });
            } else {
                setQrCodeColumnStyle({ display: "none" });
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [qrCodeIndex, setQrCodeIndex] = useState(null);

    const handleShowQrCode = (index) => {
        setQrCodeIndex(index);
    };

    return (
        <div className="w-full m-2 p-4">
            <label className="block w-full mb-2">
                {titleLength}:
                <input
                    className="w-full"
                    type="number"
                    value={length}
                    min="1"
                    max="128"
                    step="1"
                    onChange={(e) => {
                        const newValue = parseInt(e.target.value, 10);
                        if (
                            !isNaN(newValue) &&
                            newValue >= 1 &&
                            newValue <= 128
                        ) {
                            setLength(newValue);
                        }
                    }}
                />
            </label>

            <div className="w-full m-2 p-4">
                {[
                    {
                        option: "uppercase",
                        label: titleUppercase,
                        characters: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
                    },
                    {
                        option: "lowercase",
                        label: titleLowercase,
                        characters: "abcdefghijklmnopqrstuvwxyz",
                    },
                    {
                        option: "digits",
                        label: titleDigits,
                        characters: "0123456789",
                    },
                    { option: "hyphen", label: titleHyphen, characters: "-" },
                    {
                        option: "underscore",
                        label: titleUnderscore,
                        characters: "_",
                    },
                    {
                        option: "specialCharacters",
                        label: titleSpecialCharacters,
                        characters: "!@#$%^&*(){}[]=<>/,.",
                    },
                ].map(({ option, label, characters }) => (
                    <label
                        key={option}
                        className="block w-full p-2 flex items-center justify-start"
                    >
                        <Switch
                            onChange={(checked) =>
                                setOptions({ ...options, [option]: checked })
                            }
                            checked={options[option]}
                            className="mr-2"
                        />
                        <span className="text-center">
                            {label} {characters}
                        </span>
                    </label>
                ))}
            </div>

            <div
                style={{
                    borderRadius: "4px",
                    backgroundColor: "lightblue",
                    color: "darkblue",
                }}
                className="w-full flex items-center text-white text-sm font-bold px-4 py-3 mt-2 mb-2"
                role="alert"
            >
                {tip1}
            </div>

            <label className="block w-full mb-2">
                <input
                    className="w-full"
                    type="text"
                    value={customCharacters}
                    onChange={(e) => setCustomCharacters(e.target.value)}
                    placeholder="(@#?+)"
                />
            </label>

            <button
                className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg mt-4"
                onClick={handleGeneratePasswords}
                disabled={
                    Object.values(options).every((value) => !value) &&
                    !customCharacters.trim()
                }
            >
                {titleGeneratePasswords}
            </button>

            <div className="flex justify-center mt-4">
                <button
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg mr-2"
                    onClick={handleEasyPasswords}
                >
                    {titleEasy}
                </button>
                <button
                    className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg mr-2"
                    onClick={handleMediumPasswords}
                >
                    {titleMedium}
                </button>
                <button
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg"
                    onClick={handleHardPasswords}
                >
                    {titleHard}
                </button>
            </div>

            {passwords.length > 0 && (
                <table className="w-full mt-4 border m-2 p-4" ref={tableRef}>
                    <thead>
                        <tr>
                            <th className="border px-4 py-2">
                                {titlePassword}
                            </th>
                            <th
                                className="border px-4 py-2"
                                style={qrCodeColumnStyle}
                            >
                                QR Code
                            </th>
                            <th className="border px-4 py-2">{titleAction}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {passwords.map((password, index) => (
                            <tr key={index}>
                                <td
                                    className="border p-2"
                                    style={{ overflowWrap: "break-word" }}
                                >
                                    {password}
                                </td>
                                <td className="border p-2">
                                    {qrCodeIndex === index && (
                                        <QRCode value={password} />
                                    )}
                                    {qrCodeIndex !== index && (
                                        <button
                                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg mt-4"
                                            onClick={() =>
                                                handleShowQrCode(index)
                                            }
                                        >
                                            Show QR Code
                                        </button>
                                    )}
                                </td>
                                <td className="border p-2">
                                    <button
                                        className="bg-green-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg mt-4"
                                        onClick={() =>
                                            handleCopyToClipboard(
                                                password,
                                                index
                                            )
                                        }
                                    >
                                        {copiedIndex === index
                                            ? titleCopied
                                            : titleCopy}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

if (document.getElementById("password-generator")) {
    const passwordGeneratorElement =
        document.getElementById("password-generator");
    const root = createRoot(passwordGeneratorElement);
    const attributes = { ...passwordGeneratorElement.dataset };
    root.render(<PasswordGeneratorComponent attributes={attributes} />);
}
